import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useImagesContext } from '../../contexts/ImagesContext';
import { ImgClick } from '../utility';
import { connect } from 'react-redux';
import { getCoin, getMatching, getScore } from '../../services/matching';
import coinFile from './sounds/coin.ogg';
import endSeasonFile from './sounds/end-season.mp3';
import AnimationWithSound from '../utils/AnimationWithSound';
import { NumberImage } from '../utils/NumberImage';

const PAGE_SIZE = 5;

const getLevelDisplay = (level) => {
  if (level > 0) {
    return `${level} คิว`;
  } else if (level < 0) {
    return `${-level} Dan`;
  }
  return '';
};

const MatchingScore = ({ handleBack, student, config }) => {
  const { images } = useImagesContext();
  const [scoreData, setScoreData] = React.useState(null);
  const [levelList, setLevelList] = React.useState([]);
  const [playerData, setPlayerData] = React.useState(null);
  const [currentLevelIndex, setCurrentLevelIndex] = React.useState(0);
  const [currentPlayerPageIndex, setCurrentPlayerPageIndex] = React.useState(0);
  const [issueCoin, setIssueCoin] = React.useState(null);
  const [endSeasonRank, setEndSeasonRank] = React.useState(null);
  const [updateFlag, setUpdateFlag] = React.useState(false);

  const updateScore = (data) => {
    data.sort((a, b) => {
      if (b.matchLevel !== a.matchLevel) {
        return b.matchLevel - a.matchLevel;
      }
      if (a.win !== b.win) {
        return b.win - a.win;
      }
      if (a.lose !== b.lose) {
        return a.lose - b.lose;
      }
      if (b.win - b.lose !== a.win - a.lose) {
        return b.win - b.lose - (a.win - a.lose);
      }
      return b.point - b.point;
    });

    let player = data.find((v) => v.id === student.id);
    if (!player) {
      player = {
        name: student.name,
        avatar: student.avatar,
        level: student.level,
        matchLevel: student.level,
        rank: 0,
        win: 0,
        lose: 0,
        point: 0,
        score: 0,
      };
    }

    const scoreTemp = {};
    const levelTemp = [];
    const rankTemp = {};
    for (let i = 0; i < data.length; i++) {
      const v = data[i];
      if (!levelTemp.includes(v.matchLevel)) {
        levelTemp.push(v.matchLevel);
      }
      let rank = 0;
      if ((v.win && v.win > 0) || (v.lose && v.lose > 0)) {
        if (Object.hasOwnProperty.call(rankTemp, v.matchLevel)) {
          rankTemp[v.matchLevel] += 1;
        } else {
          rankTemp[v.matchLevel] = 1;
        }
        rank = rankTemp[v.matchLevel];
      }
      if (Object.hasOwnProperty.call(scoreTemp, v.matchLevel)) {
        scoreTemp[v.matchLevel].push({
          ...v,
          rank,
        });
      } else {
        scoreTemp[v.matchLevel] = [{ ...v, rank }];
      }
    }
    const playerLevelIndex = levelTemp.findIndex(
      (v) => v === player.matchLevel,
    );
    if (playerLevelIndex === -1) {
      levelTemp.push(player.matchLevel);
      scoreTemp[player.matchLevel] = [];
      player.rank = 0;
    }
    levelTemp.sort((a, b) => b - a);

    const studentLevelIndex = levelTemp.findIndex(
      (v) => v === player.matchLevel,
    );
    setCurrentLevelIndex(studentLevelIndex);
    // setPlayerData({ ...player, isSpecial: true }); // For testing special case
    const playerScoreData = scoreTemp[player.matchLevel].find(
      (v) => v.id === player.id,
    );
    setPlayerData(playerScoreData || player);
    setLevelList(levelTemp);
    setScoreData(scoreTemp);
  };

  useEffect(() => {
    getMatching(student.id).then((res) => {
      const matchings = res.data;
      if (matchings.length <= 0) {
        return;
      }
      for (let i = 0; i < matchings.length; i++) {
        const m = matchings[i];
        if (m.finishDate !== null && m.matchingStudent?.coinGoten === null) {
          setEndSeasonRank(m.matchingStudent.rank);
          setTimeout(() => {
            // Handle the case where coinGoten is null
            getCoin(student.id, m.id).then((res) => {
              // Handle the response from getCoin
              if (res.data.coin > 0) {
                setUpdateFlag(true);
                setIssueCoin(res.data.coin);
                setTimeout(() => {
                  setIssueCoin(null);
                }, 3000);
              }
            });
            setEndSeasonRank(null);
          }, 7000);
          getScore(m.id).then((res) => {
            updateScore(res.data);
          });
        }
        if (i === matchings.length - 1) {
          getScore(matchings[0].id).then((res) => {
            updateScore(res.data);
          });
        }
      }
    });
  }, []);

  const StudentItem = ({
    name,
    avatar,
    level,
    rank,
    win,
    lose,
    point,
    score,
  }) => {
    return (
      <ItemContainer>
        <ItemNumber>{rank > 0 ? rank : ''}</ItemNumber>
        <ItemImageBlock>
          <ItemPlayerImage src={avatar} />
          <ItemImageBorder
            src={images['matching-score-item-image-border.png']}
          />
          <ItemNameBackground />
          <ItemName>{name}</ItemName>
        </ItemImageBlock>
        <ItemLevel>{getLevelDisplay(level)}</ItemLevel>
        <ItemPoint>{point}</ItemPoint>
        <ItemWinBlock>
          <ItemWinBackground
            src={images['matching-score-item-win-background.png']}
          />
          <ItemWin>{win > 0 ? win : ''}</ItemWin>
        </ItemWinBlock>
        <ItemLoseBlock>
          <ItemLoseBackground
            src={images['matching-score-item-lose-background.png']}
          />
          <ItemLose>{lose > 0 ? lose : ''}</ItemLose>
        </ItemLoseBlock>
        <ItemScoreBlock>
          <ItemScoreBackground
            src={images['matching-score-item-score-background.png']}
          />
          <ItemScore>{score}</ItemScore>
        </ItemScoreBlock>
      </ItemContainer>
    );
  };

  const getScoreTable = () => {
    if (scoreData === null || levelList.length === 0) return null;
    const currentLevel = levelList[currentLevelIndex];
    const currentLevelData = scoreData[currentLevel];
    if (!currentLevelData) return null;
    return currentLevelData
      .slice(
        currentPlayerPageIndex * PAGE_SIZE,
        (currentPlayerPageIndex + 1) * PAGE_SIZE,
      )
      .map((student) => <StudentItem key={student.id} {...student} />);
  };

  return (
    <Container>
      <BackgroundImage src={images['matching-score-background.jpg']} />
      <CenterImage src={images['matching-score-board.png']} />
      <LevelName>{getLevelDisplay(levelList[currentLevelIndex])}</LevelName>
      {scoreData &&
        scoreData[levelList[currentLevelIndex]] &&
        currentPlayerPageIndex > 0 && (
          <UpButton
            onClick={() =>
              setCurrentPlayerPageIndex(currentPlayerPageIndex - 1)
            }
            src={images['matching-score-up.png']}
          />
        )}
      {scoreData &&
        scoreData[levelList[currentLevelIndex]] &&
        currentPlayerPageIndex <
          Math.ceil(
            scoreData[levelList[currentLevelIndex]].length / PAGE_SIZE,
          ) -
            1 && (
          <DownButton
            onClick={() =>
              setCurrentPlayerPageIndex(currentPlayerPageIndex + 1)
            }
            src={images['matching-score-down.png']}
          />
        )}
      {levelList.length > 0 && currentLevelIndex > 0 && (
        <BackButton
          onClick={() => setCurrentLevelIndex(currentLevelIndex - 1)}
          src={images['matching-score-back.png']}
        />
      )}
      {levelList.length > 0 && currentLevelIndex < levelList.length - 1 && (
        <ForwardButton
          onClick={() => setCurrentLevelIndex(currentLevelIndex + 1)}
          src={images['matching-score-forward.png']}
        />
      )}
      <NameBackground src={images['matching-score-name-background.png']} />
      <Name>{playerData ? playerData.name : ''}</Name>
      <PlayerImage src={playerData ? playerData.avatar : ''} />
      <ImageBorder src={images['matching-score-image-border.png']} />
      <RankBackground src={images['matching-score-rank-background.png']} />
      {playerData && playerData.isSpecial === false && playerData.rank >= 0 ? (
        <RankNumber>{playerData.rank > 0 ? playerData.rank : ''}</RankNumber>
      ) : (
        <NotRank src={images['matching-score-no-rank.png']} alt="Not rank" />
      )}
      <WinImage src={images['matching-score-win.png']} />
      <LoseImage src={images['matching-score-lose.png']} />
      <WinBackground src={images['matching-score-win-background.png']} />
      {playerData && playerData.isSpecial === true ? (
        <NotWin src={images['matching-score-no-rank.png']} alt="Not Win" />
      ) : (
        <WinNumber>{playerData?.win > 0 ? playerData.win : ''}</WinNumber>
      )}
      <LoseBackground src={images['matching-score-lose-background.png']} />
      {playerData && playerData.isSpecial === true ? (
        <NotLose src={images['matching-score-no-rank.png']} alt="Not Lose" />
      ) : (
        <LoseNumber>{playerData?.lose > 0 ? playerData.lose : ''}</LoseNumber>
      )}
      <StudentItemBlock>{getScoreTable()}</StudentItemBlock>
      <HomeButton
        src={images['matching-score-home.png']}
        onClick={() => {
          console.debug('handleBack with updateFlag', updateFlag);
          handleBack(updateFlag);
        }}
      />
      {issueCoin !== null && (
        <>
          <AnimationWithSound
            imageUrl={images[`matching-score-coin-${issueCoin}.png`]}
            soundFile={coinFile}
            playSound={config.isMutedSound === false}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '778px',
              height: '698px',
            }}
          />
        </>
      )}
      {endSeasonRank > 0 && (
        <>
          <AnimationWithSound
            imageUrl={images['matching-score-end-season.png']}
            soundFile={endSeasonFile}
            playSound={config.isMutedSound === false}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '1920px',
              height: '1080px',
            }}
          />
          <CenterBlock>
            <NumberImage
              number={endSeasonRank}
              height={180}
              prefixFileName={'matching-score-rank-'}
              images={images}
            />
          </CenterBlock>
        </>
      )}
    </Container>
  );
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
  },
  10% {
    opacity: 0;
  },
  100% {
    opacity: 1;
  }
`;

const CenterBlock = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: ${fadeIn} 8s linear;
`;

const ItemNameBackground = styled.div`
  position: absolute;
  // top: -16px;
  top: 4px;
  left: 50%;
  transform: translate(-50%);
  width: 100px;
  height: 25px;
  background-color: #fed6a3;
  border-radius: 16px;
  border: 2px solid #d6b789;
`;

const ItemName = styled.span`
  position: absolute;
  // top: -12px;
  top: 8px;
  left: 50%;
  transform: translate(-50%);
  width: 100px;
  text-align: center;
  font-family: 'PSLxText';
  font-weight: bold;
  font-size: 24px;
  line-height: 0.8;
  // text-stroke: 2px black;
  -webkit-text-stroke: 2px white;
  paint-order: stroke fill;
`;

const ItemNumber = styled.span`
  width: 85px;
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: #78522e;
  font-size: 60px;
  // border: 1px solid yellow;
`;

const ItemImageBlock = styled.div`
  position: relative;
  height: 100%;
  width: 198px;
  // border: 1px solid yellow;
`;

const ItemLevel = styled.span`
  width: 180px;
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: #78522e;
  font-size: 60px;
  // border: 1px solid yellow;
`;

const ItemPoint = styled.span`
  width: 165px;
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: #78522e;
  font-size: 60px;
  // border: 1px solid yellow;
`;

const ItemWinBackground = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ItemLoseBackground = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ItemWinBlock = styled.div`
  position: relative;
  width: 152px;
  height: 100%;
  // border: 1px solid red;
`;

const ItemWin = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: #78522e;
  font-size: 60px;
`;

const ItemLoseBlock = styled.div`
  position: relative;
  width: 166px;
  height: 100%;
  // border: 1px solid red;
`;

const ItemLose = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: #78522e;
  font-size: 60px;
`;

const ItemScoreBackground = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ItemScoreBlock = styled.div`
  position: relative;
  width: 205px;
  height: 100%;
  // border: 1px solid red;
`;

const ItemScore = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: #78522e;
  font-size: 60px;
`;

const ItemContainer = styled.div`
  position: relative;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  //border: 1px solid green;
`;

const ItemImageBorder = styled.img`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translate(-50%);
`;

const ItemPlayerImage = styled.img`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translate(-50%);
  width: 134px;
  height: 134px;
  border-radius: 50%;
`;

const StudentItemBlock = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 199px;
  left: 588px;
  // width: 900px;
  // height: 600px;
  // border: 1px solid red;
`;

const Container = styled.div`
  position: relative;
  width: 1920px;
  height: 1080px;
  overflow: hidden;
`;

const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HomeButton = styled(ImgClick)`
  position: absolute;
  left: 8px;
  bottom: 8px;
`;

const CenterImage = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const BackButton = styled(ImgClick)`
  position: absolute;
  top: 30px;
  left: 662px;
`;

const ForwardButton = styled(ImgClick)`
  position: absolute;
  top: 30px;
  left: 1170px;
`;

const UpButton = styled(ImgClick)`
  position: absolute;
  top: 500px;
  right: 50px;
`;

const DownButton = styled(ImgClick)`
  position: absolute;
  top: 700px;
  right: 50px;
`;

const NameBackground = styled.img`
  position: absolute;
  top: 150px;
  left: 134px;
`;

const LevelName = styled.span`
  position: absolute;
  top: 28px;
  left: 855px;
  width: 200px;
  text-align: center;
  font-family: PSLxText;
  font-weight: bold;
  color: black;
  -webkit-text-stroke: 3px white;
  paint-order: stroke fill;
  font-size: 64px;
  // border: 1px solid red;
`;

const Name = styled.span`
  position: absolute;
  top: 144px;
  left: 192px;
  font-family: PSLxText;
  font-weight: bold;
  color: white;
  -webkit-text-stroke: 3px black;
  paint-order: stroke fill;
  font-size: 60px;
  text-align: center;
  width: 228px;
  // border: 1px solid red;
`;

const RankNumber = styled.span`
  position: absolute;
  top: 570px;
  left: 188px;
  font-family: PSLxText;
  font-weight: bold;
  color: black;
  -webkit-text-stroke: 6px white;
  paint-order: stroke fill;
  font-size: 136px;
  text-align: center;
  width: 228px;
`;

const NotRank = styled.img`
  position: absolute;
  top: 618px;
  left: 248px;
`;

const NotWin = styled.img`
  position: absolute;
  top: 869px;
  left: 135px;
`;

const NotLose = styled.img`
  position: absolute;
  top: 869px;
  left: 363px;
`;

const ImageBorder = styled.img`
  position: absolute;
  top: 225px;
  left: 166px;
`;

const PlayerImage = styled.img`
  position: absolute;
  top: 225px;
  left: 166px;
  width: 268px;
  height: 268px;
  border-radius: 50%;
`;

const RankBackground = styled.img`
  position: absolute;
  top: 532px;
  left: 195px;
`;

const WinImage = styled.img`
  position: absolute;
  top: 796px;
  left: 118px;
`;

const WinNumber = styled.span`
  position: absolute;
  top: 855px;
  left: 122px;
  font-family: PSLxText;
  font-weight: bold;
  color: black;
  -webkit-text-stroke: 3px white;
  paint-order: stroke fill;
  font-size: 100px;
  text-align: center;
  width: 120px;
`;

const LoseImage = styled.img`
  position: absolute;
  top: 796px;
  left: 338px;
`;

const LoseNumber = styled.span`
  position: absolute;
  top: 855px;
  left: 354px;
  font-family: PSLxText;
  font-weight: bold;
  color: black;
  -webkit-text-stroke: 3px white;
  paint-order: stroke fill;
  font-size: 100px;
  text-align: center;
  width: 120px;
`;

const WinBackground = styled.img`
  position: absolute;
  top: 855px;
  left: 122px;
`;

const LoseBackground = styled.img`
  position: absolute;
  top: 855px;
  left: 350px;
`;

const mapStateToProps = ({ config, student }) => ({
  config,
  student,
});

export default connect(mapStateToProps, {})(MatchingScore);
